import client from './client';
import type { DownloadManifest, DownloadScope, DownloadTarget, ManifestFile, SkippedFile } from '../services/download/types';

/**
 * Client-side ZIP download API. The server never builds an archive — it says
 * what may be downloaded, signs URLs in batches as the browser streams, and
 * records the outcome.
 */
/** Album downloads hang off /albums/:id; event downloads off /events/:id/photos. */
function basePath(target: DownloadTarget): string {
  return target.kind === 'album' ? `/albums/${target.id}` : `/events/${target.id}/photos`;
}

export const downloadApi = {
  /** What may be downloaded, how big it is, and a token for the URL batches. */
  getManifest: (target: DownloadTarget, scope: DownloadScope, photoIds?: string[]) =>
    client
      .post<DownloadManifest>(`${basePath(target)}/download-manifest`, { scope, photoIds })
      .then((r) => r.data),

  /**
   * Fresh presigned URLs for one batch. Called repeatedly during a download so
   * URLs can't expire mid-transfer on a slow connection.
   */
  getUrls: (target: DownloadTarget, sessionToken: string, items: Array<Pick<ManifestFile, 'photoId' | 'proof'>>) =>
    client
      .post<{ urls: Record<string, string>; expiresAt: string }>(
        `${basePath(target)}/download-urls`,
        { sessionToken, items },
      )
      .then((r) => r.data),

  /**
   * Report the outcome. Photos that failed after retries get flagged for the
   * creator's corrupt-photos list. Best-effort — never blocks the user.
   */
  report: (
    target: DownloadTarget,
    sessionToken: string,
    body: { completed: number; bytes: number; durationMs: number; aborted: boolean; skipped: SkippedFile[] },
  ) =>
    client
      .post(`${basePath(target)}/download-report`, {
        sessionToken,
        completed: body.completed,
        bytes: body.bytes,
        durationMs: body.durationMs,
        aborted: body.aborted,
        skipped: body.skipped.map((s) => ({ photoId: s.photoId, proof: s.proof, reason: s.reason.slice(0, 200) })),
      })
      .catch(() => {}),

  /**
   * Fire-and-forget variant for page unload, where an XHR would be killed
   * mid-flight. Credentials are included so the session cookie rides along.
   */
  reportBeacon: (
    target: DownloadTarget,
    sessionToken: string,
    body: { completed: number; bytes: number; durationMs: number; skipped: SkippedFile[] },
  ): void => {
    try {
      const payload = JSON.stringify({
        sessionToken,
        completed: body.completed,
        bytes: body.bytes,
        durationMs: body.durationMs,
        aborted: true,
        skipped: body.skipped.map((s) => ({ photoId: s.photoId, proof: s.proof, reason: s.reason.slice(0, 200) })),
      });
      const url = `${client.defaults.baseURL ?? ''}${basePath(target)}/download-report`;
      // The API is same-origin (baseURL is relative), so an application/json
      // Blob needs no preflight and the session cookie rides along. Declaring
      // the type matters: express.json() ignores anything else, which would
      // turn the report into a silent 400 on the unload path.
      navigator.sendBeacon?.(url, new Blob([payload], { type: 'application/json' }));
    } catch {
      // Unload path — never throw.
    }
  },
};
